// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.NLQSaveDashBoardPopupActions.js" company="Izenda">
//  Copyright (c) 2020 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

import { compose, withHandlers, withState } from 'recompose';
import { curry, get, isEmpty, flow, set, clone , filter, head } from 'lodash/fp';
import Constants from 'logic/data/Izenda.ReportDesignerConstants';
import dashboardConstants from 'logic/data/Izenda.DashboardConstants';
import { concatArray } from 'share/utils/Izenda.LazyLoadingUtils';
import uiState from 'logic/Izenda.UiState';

var categorySearchKey = null;
const INTERVAL = 3000;

export default compose(
  withState('saveInto', 'changeSaveInto', props =>
    props.store.model.isGlobal ? Constants.GLOBAL_REPORT_ENUM.GLOBAL : Constants.GLOBAL_REPORT_ENUM.LOCAL
  ),
  withState(
    'model',
    'setModel',
    props => props.exploreStore.storeDashboard.model.dashboardDetail.dashboard
  ),
  withState('componentState', 'updateComponentState', () => {
    return {
      categories: [],
      isLastPageCategorySearch: false,
      isLastPageSubCategorySearch: false,
      localGlobalCategories: [],
      dashboardListData: []
    };
  }),
  withState('original', 'updateOriginalState', () => {
    return {
      reportName: '',
      reportType: '',
      categoryId: null,
      subCategoryId: null,
      isGlobal: false
    };
  }),
  withState('selectedDashboardErrorMsg', 'setSelectedDashboardErrorMsg', ''),
  withState('selectedDashboardErrorCls', 'setSelectedDashboardErrorCls', false),
  withHandlers({
    onClosing: curry(onClosing),
    onAcceptClosing: curry(onAcceptClosing),
    addNewCategoryHandler: curry(addNewCategoryHandler),
    changeSaveInHandler: curry(changeSaveInHandler),
    changeCategoryHandler: curry(changeCategoryHandler),
    getCategories: curry(getCategories),
    searchCategoryDataHandler: curry(searchCategoryDataHandler),
    resetCategory: curry(resetCategory),
    addNewDashboardList: curry(addNewDashboardList),
    changeDashboardHandler: curry(changeDashboardHandler), 
    addNewDashboardHandler: curry(addNewDashboardHandler),
    onFocusDashboardHandler: curry(onFocusDashboardHandler)
  })
);


/**
 * On closing popup handler.
 * @param {Object} props
 * @param {Object} closeCallback
 * @param {string} type
 */
function onClosing(props: any, closeCallback: any, type: string) {
  if (type === 'accept') {
    return onAcceptClosing(props);
  } else if (type === 'cancel') {
     return onCancelClosing(props);
  }
}

/**
 * When click button OK on popup
 * @param {any} props
 * @return {promise}
 */
function onAcceptClosing(props: any) {
  const {
    exploreStore: { storeDashboard }
  } = props;

  let isNewDashboard = false;
  const dashboardData = storeDashboard.model.dashboardDetail.dashboard.id;
  const newDashboardCheck = 'new_';
  if ( (dashboardData == null) || (dashboardData && dashboardData.includes(newDashboardCheck))) isNewDashboard = true;
  if (isNewDashboard) onNewSaveDashboard(props);
  else onExistingSaveDashboard(props);
}

/**
 * Handle when click cancel button
 */
function onCancelClosing(props: any) {
  props.onReject();
  props.changeCategory(null, null);
  props.changeDashboard(null, null);
}

function onNewSaveDashboard(props) {
  const {
    exploreStore: { storeDashboard },
    setSelectedDashboardErrorMsg,
    setSelectedDashboardErrorCls,
    localizer,
    model
  } = props;

  if (model && isEmpty(model.id)) {
    setSelectedDashboardErrorMsg(localizer.getText('NLQ_MODAL_DASHBOARD_NAME_MANDATORY'));
    setSelectedDashboardErrorCls(true);
    return;
  }
  storeDashboard.model.dashboardDetail.resetDashboardPart();
  return commonSaveDashboard(props);
}

function onExistingSaveDashboard(props) {
  return commonSaveDashboard(props);
}

function commonSaveDashboard(props){
  const {
    store,
    exploreStore: { storeDashboard },
    exploreStore,
    setSelectedDashboardErrorMsg,
    setSelectedDashboardErrorCls,
    localizer
  } = props;
  const dashboardPart = storeDashboard.model.dashboardDetail.addDashboardTile();
  storeDashboard.model.dashboardDetail.addDashboardPart(
    dashboardPart,
    store.model.getReportDefinition().report,
    dashboardConstants.TILE_TYPE.reportPart
  );
  storeDashboard.saveDashboard(storeDashboard.model).then(result => {
    const errorMessage = get('messages', result);
    if (errorMessage) {
      setSelectedDashboardErrorMsg(errorMessage[0].messages[0]);
      setSelectedDashboardErrorCls(true);
      return;
    } else if (result.success) {
      exploreStore.alertDetails.set({
        success: true,
        message: localizer.getText('COMMON_MESSAGES_DETAILS_WERE_SAVED')
      });
      const data = storeDashboard.model.dashboardDetail;
      props.onSaveDashboardStatus(true);
      setTimeout(function() {
        exploreStore.alertDetails.reset();
        storeDashboard.model.dashboardDetail.changeCategory(null,'');
        storeDashboard.model.dashboardDetail.changeDashboard(null,'');
      }, INTERVAL);
      props.onAccept();
    }
  });
}

function addNewCategoryHandler(props) {
  return function(category) {
    category.id = 'new_' + category.id;
    var oldState = clone(props.componentState);
    var newState = set('categories', [category])(oldState);
    props.updateComponentState(newState);
    return true;
  };
}

function addNewDashboardList(props) {
  return function(list) {
    var oldState = clone(props.componentState);
    var newState = set('dashboardListData', list)(oldState);
     props.updateComponentState(newState);
    return true;
  };
}

function addNewDashboardHandler(props) {
  return function(dashboard) {
    dashboard.id = 'new_' + dashboard.id;
    var newState = set('dashboardListData', [dashboard], props.componentState);
    props.updateComponentState(newState);
    return true;
  };
}

function changeSaveInHandler(props) {
  return function(value) {
    props.changeCategoryType(value);
    props.changeCategory(null, null);
    props.changeDashboard(null, null);
    var oldState = clone(props.componentState);
    var newState = set('categories', [])(oldState);
    props.updateComponentState(newState);
  };
}

function changeCategoryHandler(props) {
  return function(key, value) {
    props.changeCategory(key, value);
    props.changeDashboard(null, null);
  };
} 

function changeDashboardHandler(props) {
  return function(key, value) {
    props.changeDashboard(key, value);
    var oldState = clone(props.componentState);
    props.setSelectedDashboardErrorMsg('');
    props.setSelectedDashboardErrorCls(false);
    props.updateComponentState(oldState);
  };
}

function onFocusDashboardHandler(props) {
  return function() {
    props.setSelectedDashboardErrorMsg('');
    props.setSelectedDashboardErrorCls(false);
  };
}

function getCategories(props) {
  return function(pageInfo) {
    return getCategoriesData(props, pageInfo, false);
  };
}

function getCategoriesData(props, pageInfo, isReset) {
  var selectedLocalGlobalCategoryId = get(
    'id',
    getSaveInCategory(props.model.isGlobal, props.componentState.localGlobalCategories)
  );

  var request = {
    skipItems: pageInfo.fromIndex,
    pageSize: pageInfo.pageSize,
    type: 2,
    criteria: [{ key: 'name', value: categorySearchKey }],
    parentIds: [selectedLocalGlobalCategoryId],
    includeUncategory: false,
    tenantId: uiState.tenantId
  };

  return props.getCategoriesFunc(request).then(function(data) {
    var newState = clone(props.componentState);
    if (isReset) {
      newState.categories = [];
    }
    concatArray(newState.categories, data.categories, 'id', !data.isLastPage);
    newState.isLastPageCategorySearch = data.isLastPage;

    props.updateComponentState(newState);
    return data.categories;
  });
}

function searchCategoryDataHandler(props) {
  return function(key, pageInfo) {
    categorySearchKey = key;
    return getCategoriesData(props, pageInfo, true);
  };
}

function getSaveInCategory(isGlobal, localGlobalCategories) {
  return flow(filter({ isGlobal: isGlobal }), head)(localGlobalCategories);
}

function resetCategory(props) {
  return function() {
    if (categorySearchKey) {
      categorySearchKey = null;
    }
    var state = set('categories', [], props.componentState);
    props.updateComponentState(state);
  };
}



// WEBPACK FOOTER //
// ./ui/share/components/controls/NLQSaveDashBoardPopup/Izenda.NLQSaveDashBoardPopupActions.js